import { useEffect, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ClipboardList, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatCount } from '@/utils/number';
import { INITIAL_SURVEY_QUESTION } from '../../constants/survey.constants';
import type { CreateSurveyFormValues } from '../../types/survey.types';
import { QuestionBuilder } from './QuestionBuilder';

const HEADING_ID = 'survey-questions-heading';

export function SurveyQuestionsSection() {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext<CreateSurveyFormValues>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'questions',
  });

  const focusIndexRef = useRef<number | null>(null);
  const questionRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  useEffect(() => {
    if (focusIndexRef.current === null) return;
    const focusIndex = Math.min(focusIndexRef.current, questionRefs.current.length - 1);
    if (focusIndex >= 0) {
      const target = questionRefs.current[focusIndex];
      if (target) {
        target.focus();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    focusIndexRef.current = null;
  }, [fields.length]);

  function addQuestion() {
    focusIndexRef.current = fields.length;
    append(INITIAL_SURVEY_QUESTION);
    void trigger('questions');
  }

  function removeQuestion(index: number) {
    remove(index);
    void trigger('questions');
  }

  const hasError = Boolean(errors.questions?.root?.message || errors.questions?.message);
  const emptyMessage =
    errors.questions?.root?.message ??
    errors.questions?.message ??
    'Add at least one question to your survey.';

  return (
    <section className="flex flex-col gap-4" aria-labelledby={HEADING_ID}>
      <div className="flex items-center justify-between">
        <div>
          <h2 id={HEADING_ID} className="text-lg font-semibold">
            Questions
          </h2>
          {fields.length > 0 ? (
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {formatCount(fields.length, 'question')} added
            </p>
          ) : null}
        </div>
        <Button type="button" onClick={addQuestion} className="gap-1.5">
          <Plus className="size-4" />
          Add Question
        </Button>
      </div>

      {fields.length === 0 ? (
        <div
          className={cn(
            'flex flex-col items-center gap-3 rounded-xl border bg-card px-6 py-8 text-center shadow-sm',
            hasError && 'border-destructive/40',
          )}
          role={hasError ? 'alert' : undefined}
        >
          <ClipboardList
            className={cn('size-8', hasError ? 'text-destructive' : 'text-muted-foreground')}
            aria-hidden
          />
          <p
            className={cn(
              'text-sm',
              hasError ? 'font-medium text-destructive' : 'text-muted-foreground',
            )}
          >
            {emptyMessage}
          </p>
        </div>
      ) : (
        <ol className="flex flex-col gap-4">
          {fields.map((field, index) => (
            <li key={field.id}>
              <QuestionBuilder
                ref={(node) => {
                  questionRefs.current[index] = node;
                }}
                questionIndex={index}
                totalQuestions={fields.length}
                onMoveUp={() => move(index, index - 1)}
                onMoveDown={() => move(index, index + 1)}
                onRemove={() => removeQuestion(index)}
              />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
